import { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Search, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { HORECA_CATALOGUE, ALL_PRODUCTS } from "../../data/catalogue";
import { Reveal } from "./Reveal";

// ─── helpers ────────────────────────────────────────────────────────────────

const FORMAT_FILTERS = [
  { label: "All", value: "" },
  { label: "Frozen", value: "frozen" },
  { label: "Chilled", value: "chilled" },
  { label: "Ambient", value: "ambient" },
  { label: "Sweet", value: "sweet" },
  { label: "Savoury", value: "savoury" },
  { label: "Breakfast", value: "breakfast" },
];

function normalise(str) {
  return str.toLowerCase().replace(/[^a-z0-9 ]/g, "");
}

function scoreMatch(query, text) {
  const q = normalise(query);
  const t = normalise(text);
  if (t.includes(q)) return 2;
  const words = q.split(" ").filter(Boolean);
  return words.every((w) => t.includes(w)) ? 1 : 0;
}

// ─── Hero strip ──────────────────────────────────────────────────────────────

const CatalogueHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section
      ref={ref}
      // Mobile: compact — just enough for the title, no wasted space
      // Desktop: generous parallax panel
      className="relative pt-24 pb-10 md:min-h-[65vh] md:pt-36 md:pb-16 overflow-hidden flex items-end"
    >
      {/* Parallax background */}
      <motion.div style={{ y: imgY }} className="absolute inset-0 z-0">
        <img
          src="/products/hero.jpg"
          alt="Artisan Bites HORECA Collection"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2A221C]/60" />
      </motion.div>

      {/* Text */}
      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="text-xs tracking-[0.3em] uppercase font-medium text-[#D4A373] mb-3 md:mb-4"
        >
          HORECA Collection
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-[15vw] md:text-[8vw] lg:text-[7rem] leading-[0.9] tracking-tight text-[#FDFBF7]"
        >
          The Full
          <br />
          <span className="italic text-[#D4A373]">Catalogue.</span>
        </motion.h1>
        {/* Hide subtitle on mobile to keep hero tight */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 1 }}
          className="hidden md:block mt-6 max-w-md font-light text-[#FDFBF7]/70 leading-relaxed text-lg"
        >
          Seven ranges, forty-plus products. Built for hotels, restaurants and cafés.
          Search or browse the complete collection below.
        </motion.p>
      </div>
    </section>
  );
};

// ─── Search bar ──────────────────────────────────────────────────────────────

const SearchBar = ({ value, onChange }) => (
  <div className="relative group">
    <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#2A221C]/40 group-focus-within:text-[#D4A373] transition-colors" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search products…"
      className="w-full bg-[#FDFBF7] border border-[#E6E2D8] focus:border-[#D4A373] rounded-none pl-9 md:pl-11 pr-9 py-2.5 md:py-3.5 text-[#2A221C] placeholder:text-[#2A221C]/35 text-sm outline-none transition-colors"
      aria-label="Search catalogue"
    />
    <AnimatePresence>
      {value && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={() => onChange("")}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2A221C]/40 hover:text-[#2A221C] transition-colors"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </motion.button>
      )}
    </AnimatePresence>
  </div>
);

// ─── Filter pills ─────────────────────────────────────────────────────────────

const FilterPills = ({ active, onChange }) => (
  // Always a single horizontal scrollable row — no wrapping on any screen size
  <div
    className="flex gap-2 overflow-x-auto no-scrollbar"
    role="group"
    aria-label="Filter by type"
  >
    {FORMAT_FILTERS.map((f) => (
      <button
        key={f.value}
        onClick={() => onChange(f.value)}
        className={`shrink-0 rounded-full border px-3.5 md:px-4 py-1.5 text-[11px] md:text-xs tracking-[0.12em] uppercase transition-colors ${
          active === f.value
            ? "bg-[#2A221C] text-[#FDFBF7] border-[#2A221C]"
            : "bg-transparent text-[#2A221C]/70 border-[#E6E2D8] hover:border-[#2A221C]"
        }`}
      >
        {f.label}
      </button>
    ))}
  </div>
);

// ─── Search results view ─────────────────────────────────────────────────────

const SearchResults = ({ results, query }) => (
  <motion.div
    key="search-results"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
  >
    <p className="text-xs tracking-[0.2em] uppercase text-[#B07D4E] mb-8">
      {results.length} result{results.length !== 1 ? "s" : ""} for &ldquo;{query}&rdquo;
    </p>
    {results.length === 0 ? (
      <div className="py-20 text-center">
        <p className="font-display text-4xl md:text-5xl text-[#2A221C]/20">Nothing found.</p>
        <p className="mt-4 text-[#2A221C]/50 font-light">Try a different term — flavour, format, or category.</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E6E2D8] border border-[#E6E2D8]">
        {results.map((product, i) => (
          <motion.div
            key={`${product.categoryId}-${product.name}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
            className="bg-[#FDFBF7] p-4 md:p-8 flex flex-col gap-3 group"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-[9px] tracking-[0.25em] uppercase text-[#D4A373]">
                {product.categoryName}
              </span>
              <span className="text-[9px] tracking-[0.2em] uppercase text-[#2A221C]/40 border border-[#E6E2D8] px-2 py-0.5 shrink-0">
                {product.categoryFormat}
              </span>
            </div>
            <h3 className="font-display text-xl md:text-2xl text-[#2A221C] leading-tight">
              {product.name}
            </h3>
            <p className="text-sm font-light text-[#2A221C]/65 leading-relaxed flex-1">
              {product.desc}
            </p>
            <div className="flex flex-wrap gap-1 mt-1">
              {product.tags.slice(0, 3).map((t) => (
                <span key={t} className="text-[9px] tracking-[0.15em] uppercase bg-[#F5F2EB] text-[#2A221C]/50 px-2 py-0.5">
                  {t}
                </span>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    )}
  </motion.div>
);

// ─── Category card ────────────────────────────────────────────────────────────

const CategoryCard = ({ cat, index }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Reveal delay={index * 0.08} className="border border-[#E6E2D8] overflow-hidden">
      {/* Image + overlay */}
      <div className="relative overflow-hidden aspect-[16/9] group">
        <img
          src={cat.image}
          alt={cat.category}
          className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A221C]/70 via-[#2A221C]/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
          <span className="text-[9px] tracking-[0.25em] uppercase text-[#FDFBF7]/70">{cat.format}</span>
          <p className="font-display italic text-2xl md:text-3xl text-[#FDFBF7] mt-0.5">
            {cat.category}
          </p>
        </div>
        <span className="absolute top-4 left-5 font-display text-5xl text-[#FDFBF7]/15 select-none leading-none">
          {cat.index}
        </span>
      </div>

      {/* Body */}
      <div className="p-4 md:p-7 bg-[#FDFBF7]">
        <p className="text-sm font-light text-[#2A221C]/65 leading-relaxed mb-5">
          {cat.description}
        </p>

        {/* Product count + expand toggle */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="group flex items-center justify-between w-full text-left border-t border-[#E6E2D8] pt-4"
          aria-expanded={expanded}
        >
          <span className="text-xs tracking-[0.2em] uppercase text-[#2A221C]/60 font-medium">
            Products
          </span>
          <motion.span
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="text-[#B07D4E]"
          >
            <ChevronDown className="h-4 w-4" />
          </motion.span>
        </button>

        {/* Expandable product list */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.ul
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="pt-4 space-y-0">
                {cat.products.map((p, i) => (
                  <motion.li
                    key={p.name}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, delay: i * 0.05 }}
                    className="border-b border-[#E6E2D8] py-4 last:border-b-0"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="font-body font-medium text-[#2A221C] text-sm md:text-base">{p.name}</p>
                        <p className="mt-1 text-xs md:text-sm font-light text-[#2A221C]/60 leading-relaxed">
                          {p.desc}
                        </p>
                      </div>
                      {p.serves && (
                        <span className="shrink-0 text-[9px] tracking-[0.15em] uppercase text-[#2A221C]/40 mt-0.5">
                          {p.serves}
                        </span>
                      )}
                    </div>
                  </motion.li>
                ))}
              </div>
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
};

// ─── Browse view ──────────────────────────────────────────────────────────────

const BrowseView = ({ categories }) => (
  <motion.div
    key="browse"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-8"
  >
    {categories.map((cat, i) => (
      <CategoryCard key={cat.id} cat={cat} index={i} />
    ))}
  </motion.div>
);

// ─── Main Catalogue component ─────────────────────────────────────────────────

export const Catalogue = () => {
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("");

  // Filtered categories for browse view
  const filteredCategories = useMemo(() => {
    if (!activeFilter) return HORECA_CATALOGUE;
    return HORECA_CATALOGUE.filter((cat) =>
      cat.tags.some((t) => t.toLowerCase().includes(activeFilter))
    );
  }, [activeFilter]);

  // Search results across all products
  const searchResults = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.trim();
    return ALL_PRODUCTS
      .map((p) => ({
        ...p,
        score:
          scoreMatch(q, p.name) * 3 +
          scoreMatch(q, p.desc) +
          scoreMatch(q, p.categoryName) +
          (p.tags.some((t) => normalise(t).includes(normalise(q))) ? 1 : 0),
      }))
      .filter((p) => p.score > 0)
      .sort((a, b) => b.score - a.score);
  }, [query]);

  const isSearching = query.trim().length > 0;

  return (
    <div className="grain font-body bg-[#FDFBF7] text-[#2A221C] antialiased min-h-screen">
      <CatalogueHero />

      {/* ── Search & filter bar ──
          Mobile:  NOT sticky — scrolls with page so it doesn't eat viewport
          md+:     sticky just below the navbar (top-20 = 80px navbar height)
      ── */}
      <div className="md:sticky md:top-20 z-30 bg-[#FDFBF7]/95 backdrop-blur-md border-b border-[#E6E2D8]">
        <div className="mx-auto max-w-[1400px] px-4 md:px-10 py-3 md:py-3.5 flex items-center gap-3">
          {/* Search — fixed width on desktop, fills remaining space on mobile */}
          <div className="w-full md:w-72 lg:w-80 shrink-0">
            <SearchBar value={query} onChange={setQuery} />
          </div>

          {/* Filter pills — horizontal scroll row, hidden when searching */}
          {!isSearching && (
            <div className="flex-1 min-w-0">
              <FilterPills active={activeFilter} onChange={setActiveFilter} />
            </div>
          )}

          {/* "Browse all" back link when searching */}
          {isSearching && (
            <button
              onClick={() => setQuery("")}
              className="shrink-0 text-xs tracking-[0.2em] uppercase text-[#2A221C]/50 hover:text-[#D4A373] transition-colors whitespace-nowrap"
            >
              ← All
            </button>
          )}
        </div>
      </div>

      {/* ── Main content ── */}
      <main className="mx-auto max-w-[1400px] px-4 md:px-10 py-8 md:py-20">
        <AnimatePresence mode="wait">
          {isSearching ? (
            <SearchResults key="search" results={searchResults} query={query} />
          ) : (
            <BrowseView key="browse" categories={filteredCategories} />
          )}
        </AnimatePresence>
      </main>

      {/* ── Bottom CTA ── */}
      <Reveal>
        <div className="bg-[#2A221C] text-[#FDFBF7] py-16 md:py-20 px-6 md:px-10">
          <div className="mx-auto max-w-[1400px] flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#D4A373] mb-3">Interested?</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight">
                Request samples or<br />
                <span className="italic text-[#D4A373]">wholesale pricing.</span>
              </h2>
            </div>
            <a
              href="/#enquire"
              className="group inline-flex items-center gap-3 border border-[#FDFBF7]/30 px-7 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#D4A373] hover:border-[#D4A373] transition-colors whitespace-nowrap"
            >
              <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              Get in Touch
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  );
};
